import React, {useEffect, useRef, useState} from 'react';
import ApiDatabase from "server";
import UseContext from "hooks/useContext";
import UseModal from "components/modal/useModal";
import Button from "components/button";
import Chip from "components/chip/chip";
import {AddIcon, Delete, Dots, Modify} from "assets/icons";
import useFetchData from "hooks/useFetchData";

const ROLE_LABELS = {
    200: 'Observateur',
    205: 'Manageur',
    210: 'Responsable',
    215: 'Directeur',
    220: 'Superviseur',
    225: 'Administrateur',
};

const TeamMemberRow = ({member}) => {
    const {toggle} = UseModal();
    const {setAdminData, companyData} = UseContext();
    const {userData} = useFetchData();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const getInitials = () => {
        const firstInitial = (member.firstname || '').charAt(0).toUpperCase();
        const lastInitial = (member.lastname || '').charAt(0).toUpperCase();
        return `${firstInitial}${lastInitial}`.trim() || '--';
    };

    const getChipColor = () => userData.email === member.email ? 'WARNING' : 'PRIMARY';

    const setSelectedMember = () => {
        setAdminData({
            id: member._id,
            id_company: companyData._id,
            email: member.email,
            firstname: member.firstname,
            lastname: member.lastname,
            role: member.role,
        });
    };

    const handleModify = (event) => {
        event.preventDefault();
        setSelectedMember();
        setIsOpen(false);
        toggle('admin_u_modify');
    };

    const handleDelete = (event) => {
        event.preventDefault();
        setSelectedMember();
        setIsOpen(false);
        toggle('admin_d_delete');
    };

    return (
        <div className="w-full bg-white border border-gray-100 rounded-lg px-4 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
                <div className="min-w-[40px] w-[40px] h-[40px] rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                    <span className="text-sm font-medium text-gray-700">{getInitials()}</span>
                </div>
                <div className="flex flex-col min-w-0">
                    <p className="text-gray-900 truncate">
                        {member.firstname} {member.lastname} {member.role === 225 ? '(vous)' : null}
                    </p>
                    <p className="text-gray-500 truncate">{member.email}</p>
                </div>
            </div>

            <div className="flex items-center gap-4 shrink-0">
                <div className="flex gap-3">
                    <Chip type={'INDICATOR'} color={getChipColor()}>{ROLE_LABELS[member.role]}</Chip>
                    {member.validation !== 2 ? <Chip type={'INDICATOR'} color={'PRIMARY'}>En attente</Chip> : null}
                </div>
                {member.role === 225 ? null : (
                    <div className="relative" ref={dropdownRef}>
                        <Button size={'TABLE'} color={'TABLE'} onClick={() => setIsOpen(!isOpen)}>
                            <Dots w={14} h={4} color={'#374151'}/>
                        </Button>
                        {isOpen && (
                            <div className="absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg w-48 px-4 py-2 top-10 right-0 z-30 bg-gray-50 flex flex-col gap-2">
                                <button className="flex flex-row items-center gap-3 cursor-pointer ft-sm hover:bg-gray-100 rounded px-2 py-1 text-left" onClick={handleModify}>
                                    <Modify wh={14} color={'#111827'}/>
                                    Modifier
                                </button>
                                <button className="flex flex-row items-center gap-3 cursor-pointer ft-sm text-red-600 hover:bg-gray-100 rounded px-2 py-1 text-left" onClick={handleDelete}>
                                    <Delete wh={14} color={'#DC2626'}/>
                                    Supprimer
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const TeamContent = () => {
    const {companyData, setStaffData, staffData, refreshStaff, rest} = UseContext();
    const {toggle} = UseModal();

    useEffect(() => {
        if (Object.keys(companyData).length) {
            ApiDatabase.postStaffArray(
                {staff: companyData.employees, id_company: companyData._id},
                setStaffData,
                (err) => console.log('Error fetching staff:', err)
            );
        }
    }, [companyData, refreshStaff, setStaffData]);

    const handleAdd = (event) => {
        event.preventDefault();
        toggle('admin_c_add');
    };

    return (
        <div className="w-full px-2 tab:px-4 lg:px-8 py-8 mb-28 flex flex-col gap-8">
            <div className="flex items-center justify-between gap-4">
                <h2 className="text-xl font-medium text-gray-900">Équipe</h2>
                {rest === 0 ? null : (
                    <Button size={'LGI'} color={'PRIMARYFIXE'} onClick={handleAdd}>
                        <AddIcon wh={14} color={'#ffffff'}/>
                        Inviter un membre
                    </Button>
                )}
            </div>
            <div className="flex flex-col gap-3">
                {(staffData || []).map((member) => (
                    <TeamMemberRow key={member._id} member={member}/>
                ))}
            </div>
        </div>
    );
};

export default TeamContent;
